import * as React from "react";
import { ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

export type SegmentedControlOption<Value extends string> = {
  value: Value;
  label: string;
  /** Shown left of the label (e.g. a small icon on one option only). */
  icon?: React.ReactNode;
  "aria-label"?: string;
};

export type SegmentedControlProps<Value extends string> = {
  value: Value;
  onChange: (value: Value) => void;
  options: ReadonlyArray<SegmentedControlOption<Value>>;
  "aria-label": string;
  sx?: SxProps<Theme>;
};

/** Fully rounded, whatever the height. */
const PILL = 999;

/** One pill-shaped, bordered track holding a small set of mutually
 * exclusive options (Group / Sort / Show filters in the sessions
 * sidebar). The active option renders as a filled, rounded pill in a
 * lighter surface colour with bold text; inactive options are plain
 * text — no per-button border, so the track reads as one control rather
 * than three buttons. Built on MUI's ToggleButtonGroup, so every option
 * is a real button with ``aria-pressed``, and the group is one tab
 * stop (MUI's roving tabindex): arrow keys move focus within it, Enter /
 * Space picks the focused option. All colours are theme tokens,
 * so it follows light/dark automatically.
 *
 * The track may shrink with a narrow sidebar: labels then end in an
 * ellipsis, and hovering or focusing a cut-off label shows it in full. */
export function SegmentedControl<Value extends string>({
  value,
  onChange,
  options,
  sx,
  ...rest
}: SegmentedControlProps<Value>): React.ReactElement {
  const ariaLabel = rest["aria-label"];
  return (
    <ToggleButtonGroup
      size="small"
      value={value}
      exclusive
      onChange={(_e, next: Value | null) => {
        if (next) onChange(next);
      }}
      aria-label={ariaLabel}
      sx={[
        {
          border: 1,
          borderColor: "divider",
          borderRadius: `${PILL}px`,
          p: "2px",
          gap: "2px",
          bgcolor: "transparent",
          minWidth: 0,
          maxWidth: "100%",
          "& .MuiToggleButtonGroup-grouped": {
            border: 0,
            borderRadius: `${PILL}px !important`,
            margin: 0,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {options.map((option) => (
        <SegmentButton key={option.value} option={option} />
      ))}
    </ToggleButtonGroup>
  );
}

/** One option. ToggleButtonGroup reaches its buttons through context,
 * so the Tooltip wrapper doesn't break the group. The tooltip opens only while the label is cut off. */
function SegmentButton<Value extends string>({
  option,
}: {
  option: SegmentedControlOption<Value>;
}): React.ReactElement {
  const labelRef = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);
  return (
    <Tooltip
      title={option.label}
      open={open}
      onOpen={() => {
        const el = labelRef.current;
        setOpen(!!el && el.scrollWidth > el.clientWidth);
      }}
      onClose={() => setOpen(false)}
    >
      <ToggleButton
        value={option.value}
        aria-label={option["aria-label"]}
        disableRipple
        sx={{
          minWidth: 0,
          flex: "0 1 auto",
          px: 1,
          py: 0.25,
          fontSize: 11,
          lineHeight: 1.6,
          textTransform: "none",
          whiteSpace: "nowrap",
          color: "text.secondary",
          fontWeight: 400,
          "& svg": { flexShrink: 0 },
          "&.Mui-selected": {
            bgcolor: "action.selected",
            color: "text.primary",
            fontWeight: 700,
          },
          "&.Mui-selected:hover": {
            bgcolor: "action.selected",
          },
        }}
      >
        {option.icon}
        <span
          ref={labelRef}
          style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {option.label}
        </span>
      </ToggleButton>
    </Tooltip>
  );
}
